import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import FractionInput from "../../../../components/inputs/FractionInput";

// === utils (jak w sinusie) ===
function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatCosAsSqrtFraction({ a, b, vertex }) {
  const adjacent = vertex === "B" ? a : b;
  const hypotenuse = Math.sqrt(a * a + b * b);

  if (Number.isInteger(hypotenuse)) {
    const divisor = gcd(adjacent, hypotenuse);
    const num = adjacent / divisor;
    const den = hypotenuse / divisor;
    const latex = den === 1 ? `${num}` : `\\dfrac{${num}}{${den}}`;
    return { latex, value: adjacent / hypotenuse };
  }

  const sqrt = simplifySqrt(a * a + b * b);
  const fullNumerator = adjacent * sqrt.outside;
  const fullDenominator = a * a + b * b;
  const divisor = gcd(fullNumerator, fullDenominator);
  const num = fullNumerator / divisor;
  const den = fullDenominator / divisor;
  const sqrtLatex = `\\sqrt{${sqrt.inside}}`;
  const latex =
    den === 1
      ? `${num === 1 ? "" : num}${sqrtLatex}`
      : `\\dfrac{${num === 1 ? "" : num}${sqrtLatex}}{${den}}`;

  return {
    latex,
    value: adjacent / hypotenuse,
  };
}

// === API ===

export function generateTask(options = {}) {
  const { withAngles = false } = options;

  let a, b, hypotenuse;
  do {
    a = Math.floor(Math.random() * 8) + 2;
    b = Math.floor(Math.random() * 8) + 2;
    hypotenuse = Math.sqrt(a * a + b * b);
  } while (Number.isInteger(hypotenuse) || a === b);

  const vertex = Math.random() < 0.5 ? "B" : "C";
  const baseData = { a, b, vertex };
  const { latex, value } = formatCosAsSqrtFraction(baseData);
  const hypotenuseSquared = a * a + b * b;

  let alphaDeg = null;
  let betaDeg = null;

  if (withAngles) {
    const alphaRad = Math.acos(value);
    const alpha = (alphaRad * 180) / Math.PI;
    alphaDeg = Math.round(alpha);
    betaDeg = 90 - alphaDeg;
  }

  return {
    type: "FindTriangleFromCosTask",
    data: {
      a,
      b,
      vertex,
      cosLatex: latex,
      cosValue: value,
      hypotenuse: Math.sqrt(hypotenuseSquared),
      withAngles,
      alphaDeg,
      betaDeg,
    },
    expectedAnswer: { a, b },
  };
}

export function renderTask({ data }) {
  const { a, b, vertex, cosLatex, withAngles } = data;
  return (
    <>
      <p>
        Na podstawie rysunku poniżej, oblicz długości przyprostokątnych{" "}
        <InlineMath math="a" /> i <InlineMath math="b" />. Kąt{" "}
        <InlineMath math={'\\alpha'} /> znajduje się w wierzchołku{" "}
        <InlineMath math={vertex} />, a{" "}
        <InlineMath math={`\\cos\\alpha = ${cosLatex}`} />.
      </p>
      {withAngles && (
        <p>
          Dodatkowo podaj miary kątów{" "}
          <InlineMath math={'\\alpha'} /> (zaznaczonym na rysunku) i{" "}
          <InlineMath math={'\\beta'} /> (drugim kątem ostrym tego trójkąta
          prostokątnego) w stopniach, z dokładnością do{" "}
          <InlineMath math={'1^\\circ'} />.
        </p>
      )}
      <TriangleDiagram
        a={a}
        b={b}
        angleAt={vertex}
        labelSides={{ a: "a", b: "b" }}
      />
    </>
  );
}



export function renderInput({ data }, userAnswer, onChange, disabled) {
  const ans = userAnswer || {};

  return (
    <div className="flex flex-col gap-4">
      {/* boki */}
      <div className="flex items-center gap-2">
        <InlineMath math="a =" />
        <FractionInput
          value={ans.a || ""}
          onChange={(val) => onChange({ ...ans, a: val })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math="b =" />
        <FractionInput
          value={ans.b || ""}
          onChange={(val) => onChange({ ...ans, b: val })}
          disabled={disabled}
          label="b"
        />
      </div>

      {/* kąty – tylko w trybie rozszerzonym */}
      {data.withAngles && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math="\alpha =" />
            <FractionInput
              value={ans.alpha || ""}
              onChange={(val) => onChange({ ...ans, alpha: val })}
              disabled={disabled}
              label="\alpha"
              suffixLatex="^\circ"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math="\beta =" />
            <FractionInput
              value={ans.beta || ""}
              onChange={(val) => onChange({ ...ans, beta: val })}
              disabled={disabled}
              label="\beta"
              suffixLatex="^\circ"
            />
          </div>
        </>
      )}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  // brak odpowiedzi
  if (!userAnswer || typeof userAnswer !== "object") {
    if (!data.withAngles) return false;
    return {
      isCorrect: false,
      score: 0,
      maxScore: 2,
      parts: { boki: false, kąty: false },
    };
  }

  const parsedA = userAnswer.a?.parsed ?? null;
  const parsedB = userAnswer.b?.parsed ?? null;

  const okA = parsedA !== null && parsedA === data.a;
  const okB = parsedB !== null && parsedB === data.b;
  const okSides = okA && okB;

  // jeśli nie ma kątów – proste 1-punktowe zadanie (boolean)
  if (!data.withAngles) {
    return okSides;
  }

  const alphaParsed = userAnswer.alpha?.parsed ?? null;
  const betaParsed = userAnswer.beta?.parsed ?? null;

  const alphaOk =
    alphaParsed !== null && Math.round(alphaParsed) === data.alphaDeg;
  const betaOk =
    betaParsed !== null && Math.round(betaParsed) === data.betaDeg;
  const okAngles = alphaOk && betaOk;

  // punktacja: 1 pkt za komplet boków, 1 pkt za oba kąty
  let score = 0;
  if (okSides) score += 1;
  if (okAngles) score += 1;

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      boki: okSides,
      kąty: okAngles,
    },
  };
}

export function renderAnswer({ data }) {
  const { a, b, withAngles, alphaDeg, betaDeg } = data;
  return (
    <div className="space-y-2 mt-2">
      <div>
        <InlineMath math={`a = ${a}`} />
      </div>
      <div>
        <InlineMath math={`b = ${b}`} />
      </div>
      {withAngles && (
        <>
          <div>
            <InlineMath math={`\\alpha \\approx ${alphaDeg}^\\circ`} />
          </div>
          <div>
            <InlineMath math={`\\beta \\approx ${betaDeg}^\\circ`} />
          </div>
        </>
      )}
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    (!userAnswer.a?.raw && !userAnswer.b?.raw)
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const alphaPart =
    userAnswer.alpha?.raw != null
      ? `,\\ \\alpha = ${userAnswer.alpha.raw}^\\circ`
      : "";
  const betaPart =
    userAnswer.beta?.raw != null
      ? `,\\ \\beta = ${userAnswer.beta.raw}^\\circ`
      : "";

  return (
    <InlineMath
      math={`a = ${userAnswer.a?.raw || "?"},\\ b = ${
        userAnswer.b?.raw || "?"
      }${alphaPart}${betaPart}`}
    />
  );
}

export function renderExplanation({ data }) {
  const { vertex, cosLatex, a, b, withAngles, alphaDeg, betaDeg } = data;

  const hypSquared = a * a + b * b;
  const { outside, inside } = simplifySqrt(hypSquared);
  const hypLatex =
    outside === 1 ? `\\sqrt{${inside}}` : `${outside}\\sqrt{${inside}}`;

  const adjacent = vertex === "B" ? a : b;
  const adjacentLabel = vertex === "B" ? "a" : "b";
  const opposite = vertex === "B" ? b : a;
  const oppositeLabel = vertex === "B" ? "b" : "a";

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Z definicji:
        <BlockMath math={`\\cos\\alpha = \\dfrac{\\text{przyprostokątna~przyległa~do~}\\alpha}{\\text{przeciwprostokątna}}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy dane:
        <BlockMath
          math={`\\cos\\alpha = ${cosLatex} = \\dfrac{${adjacentLabel}}{${hypLatex}}`}
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Wyznaczamy przyprostokątną przyległą:
        <BlockMath math={`${adjacentLabel} = ${cosLatex}\\cdot ${hypLatex}`} />
        <BlockMath math={`${adjacentLabel} = ${adjacent}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Liczymy drugą przyprostokątną z Pitagorasa:
        <BlockMath math={`a^2 + b^2 = c^2`} />
        <BlockMath
          math={`${oppositeLabel}^2 = ${hypSquared} - ${adjacentLabel}^2`}
        />
        <BlockMath math={`${oppositeLabel} = ${opposite}`} />
      </div>

      {withAngles && (
        <div>
          <strong>Krok 5:</strong> Liczymy kąty.
          <BlockMath math={`\\cos\\alpha = ${cosLatex}`} />
          <BlockMath math={`\\alpha \\approx ${alphaDeg}^\\circ`} />
          <BlockMath math={`\\beta = 90^\\circ - \\alpha \\approx ${betaDeg}^\\circ`} />
        </div>
      )}

      <div>
        <strong>Odpowiedź końcowa:</strong>
        <BlockMath
          math={
            withAngles
              ? `a = ${a},\\ b = ${b},\\ \\alpha \\approx ${alphaDeg}^\\circ,\\ \\beta \\approx ${betaDeg}^\\circ`
              : `a = ${a},\\ b = ${b}`
          }
        />
      </div>
    </div>
  );
}
